import React, { useState } from "react";
import { useLang } from "@/context/LangContext";
import { CONTACT } from "@/constants/testIds";
import { Phone, MapPin, Send, CheckCircle2, AlertCircle } from "lucide-react";

const initial = { name: "", email: "", phone: "", service: "", message: "" };

const Contact = () => {
  const { lang, t } = useLang();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState(null); // 'sending'|'success'|'error'

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    
    try {
      // Folosim FormSubmit ca poștaș invizibil
      await fetch("https://formsubmit.co/ajax/consultatii.avocatorura@gmail.com", {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          "Subiect": `[Site] Solicitare consultație – ${form.name}`,
          "Nume": form.name,
          "Email": form.email,
          "Telefon": form.phone || "Nu a completat",
          "Serviciu solicitat": form.service || "Nu a selectat",
          "Mesaj": form.message
        })
      });

      setStatus("success");
      setForm(initial); // Golește câmpurile după trimitere
    } catch (err) {
      console.error("Eroare la trimitere:", err);
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#FBF9F6] relative">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left - Info */}
        <div className="lg:col-span-5">
          <p className="overline">{t.contact.overline}</p>
          <h2 className="display mt-5 text-4xl md:text-5xl lg:text-6xl text-[#1A0010]">
            {t.contact.title}
          </h2>
          <div className="hairline-strong w-20 mt-8" />
          <p className="mt-8 text-[15px] md:text-base text-[#4D3D46] leading-relaxed max-w-md">
            {t.contact.intro}
          </p>

          <div className="mt-12 space-y-7">
            <div className="flex items-start gap-5">
              <span className="mt-1 text-[#66023C]"><Phone size={18} strokeWidth={1.4} /></span>
              <div>
                <div className="overline !text-[#4D3D46]">{t.contact.phoneLabel}</div>
                <a href="tel:+40720201887" className="serif text-2xl text-[#1A0010] hover:text-[#66023C] transition-colors">
                  0720 201 887
                </a>
              </div>
            </div>
            <div className="flex items-start gap-5">
              <span className="mt-1 text-[#66023C]"><MapPin size={18} strokeWidth={1.4} /></span>
              <div>
                <div className="overline !text-[#4D3D46]">{t.contact.addressLabel}</div>
                <div className="serif text-xl text-[#1A0010]">{t.contact.address}</div>
                <div className="text-sm text-[#4D3D46] mt-1">{t.contact.bar}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="border border-[#1A0010]/15 p-8 md:p-12 bg-[#FBF9F6] grid grid-cols-1 md:grid-cols-2 gap-7"
          >
            <div className="md:col-span-1">
              <label className="label">{t.contact.name} *</label>
              <input
                data-testid={CONTACT.name}
                type="text"
                required
                className="field"
                value={form.name}
                onChange={setField("name")}
              />
            </div>
            <div className="md:col-span-1">
              <label className="label">{t.contact.email} *</label>
              <input
                data-testid={CONTACT.email}
                type="email"
                required
                className="field"
                value={form.email}
                onChange={setField("email")}
              />
            </div>
            <div className="md:col-span-1">
              <label className="label">{t.contact.phone}</label>
              <input
                data-testid={CONTACT.phone}
                type="tel"
                className="field"
                value={form.phone}
                onChange={setField("phone")}
              />
            </div>
            <div className="md:col-span-1">
              <label className="label">{t.contact.service}</label>
              <select
                data-testid={CONTACT.service}
                className="field cursor-pointer"
                value={form.service}
                onChange={setField("service")}
              >
                <option value="" disabled>Selectează un serviciu...</option>
                <option value="Consultații juridice">01 - Consultații juridice</option>
                <option value="Asistență & reprezentare">02 - Asistență & reprezentare</option>
                <option value="Redactare acte juridice">03 - Redactare acte juridice</option>
                <option value="Apărare cauze penale">04 - Apărare cauze penale</option>
                <option value="Dreptul familiei">05 - Dreptul familiei</option>
                <option value="Drept comercial">06 - Drept comercial</option>
                <option value="Altul">Altul (Vă rog detaliați mai jos)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="label">{t.contact.message} *</label>
              <textarea
                data-testid={CONTACT.message}
                required
                className="field"
                value={form.message}
                onChange={setField("message")}
              />
            </div>

            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-2">
              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={status === "sending"}
                className="btn-imperial"
              >
                {status === "sending" ? t.contact.sending : t.contact.submit}
                <Send size={13} strokeWidth={1.5} />
              </button>

              {status === "success" && (
                <div
                  data-testid={CONTACT.success}
                  className="flex items-center gap-2 text-[#66023C] text-sm"
                >
                  <CheckCircle2 size={16} /> {t.contact.success}
                </div>
              )}
              {status === "error" && (
                <div
                  data-testid={CONTACT.error}
                  className="flex items-center gap-2 text-red-700 text-sm"
                >
                  <AlertCircle size={16} /> {t.contact.error}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;